import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface PaymentSettingsData {
  id: string;
  flutterwave_public_key: string;
  flutterwave_secret_key: string;
  encryption_key: string;
  consultation_fee: number;
  currency: string;
  updated_at: string;
}

interface PaymentSettingsProps {
  onToast: (message: string, type: 'success' | 'error') => void;
}

const CURRENCY_OPTIONS = [
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
];

export default function PaymentSettings({ onToast }: PaymentSettingsProps) {
  const [settings, setSettings] = useState<PaymentSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [encryptionKey, setEncryptionKey] = useState('');
  const [fee, setFee] = useState('');
  const [currency, setCurrency] = useState('NGN');
  const [showKey, setShowKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showEncryptionKey, setShowEncryptionKey] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('payment_settings')
      .select('*')
      .limit(1);

    if (!error && data && data.length > 0) {
      const s = data[0];
      setSettings(s);
      setPublicKey(s.flutterwave_public_key);
      setSecretKey(s.flutterwave_secret_key || '');
      setEncryptionKey(s.encryption_key || '');
      setFee(String(s.consultation_fee));
      setCurrency(s.currency);
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    if (!publicKey.trim()) {
      onToast('Please enter your Flutterwave public key', 'error');
      return;
    }
    if (!secretKey.trim()) {
      onToast('Please enter your Flutterwave secret key', 'error');
      return;
    }
    if (!encryptionKey.trim()) {
      onToast('Please enter your Flutterwave encryption key', 'error');
      return;
    }
    if (!fee || Number(fee) <= 0) {
      onToast('Please enter a valid consultation fee', 'error');
      return;
    }

    setIsSaving(true);
    try {
      if (settings) {
        const { error } = await supabase
          .from('payment_settings')
          .update({
            flutterwave_public_key: publicKey.trim(),
            flutterwave_secret_key: secretKey.trim(),
            encryption_key: encryptionKey.trim(),
            consultation_fee: Number(fee),
            currency,
            updated_at: new Date().toISOString(),
          })
          .eq('id', settings.id);

        if (error) {
          console.error('Save error:', error);
          onToast('Failed to save settings', 'error');
        } else {
          onToast('Payment settings saved successfully', 'success');
          fetchSettings();
        }
      } else {
        const { error } = await supabase
          .from('payment_settings')
          .insert({
            flutterwave_public_key: publicKey.trim(),
            flutterwave_secret_key: secretKey.trim(),
            encryption_key: encryptionKey.trim(),
            consultation_fee: Number(fee),
            currency,
          });

        if (error) {
          console.error('Insert error:', error);
          onToast('Failed to save settings', 'error');
        } else {
          onToast('Payment settings saved successfully', 'success');
          fetchSettings();
        }
      }
    } catch {
      onToast('Something went wrong', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const currencyInfo = CURRENCY_OPTIONS.find((c) => c.code === currency);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-sm text-gray-500">Loading payment settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {/* Flutterwave API Key */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <i className="ri-key-2-line text-lg text-orange-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Flutterwave Public Key</h3>
            <p className="text-xs text-gray-500">Your public key for processing payments</p>
          </div>
        </div>

        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={publicKey}
            onChange={(e) => setPublicKey(e.target.value)}
            placeholder="FLWPUBK-xxxxxxxxxxxxxxxx-X"
            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono"
          />
          <button
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className={`${showKey ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
          </button>
        </div>

        <div className="flex items-start gap-2 mt-3 bg-amber-50 rounded-lg p-3">
          <i className="ri-information-line text-amber-600 text-sm mt-0.5"></i>
          <p className="text-xs text-amber-700">
            Get your public key from your{' '}
            <a
              href="https://dashboard.flutterwave.com/settings/apis"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="underline font-medium cursor-pointer"
            >
              Flutterwave Dashboard
            </a>
            .
          </p>
        </div>
      </div>

      {/* Flutterwave Secret Key */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-lg text-red-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Flutterwave Secret Key</h3>
            <p className="text-xs text-gray-500">Your secret key for verifying transactions</p>
          </div>
        </div>

        <div className="relative">
          <input
            type={showSecretKey ? 'text' : 'password'}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            placeholder="FLWSECK-xxxxxxxxxxxxxxxx-X"
            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono"
          />
          <button
            onClick={() => setShowSecretKey(!showSecretKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className={`${showSecretKey ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
          </button>
        </div>

        <div className="flex items-start gap-2 mt-3 bg-red-50 rounded-lg p-3">
          <i className="ri-error-warning-line text-red-500 text-sm mt-0.5"></i>
          <p className="text-xs text-red-600">
            Keep this key confidential. It is used server-side to verify payment transactions. Never expose it in client-side code.
          </p>
        </div>
      </div>

      {/* Flutterwave Encryption Key */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
            <i className="ri-lock-password-line text-lg text-violet-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Encryption Key</h3>
            <p className="text-xs text-gray-500">Used to encrypt payment data for secure processing</p>
          </div>
        </div>

        <div className="relative">
          <input
            type={showEncryptionKey ? 'text' : 'password'}
            value={encryptionKey}
            onChange={(e) => setEncryptionKey(e.target.value)}
            placeholder="FLWSECK_TESTxxxxxxxxxxxxxxxx"
            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono"
          />
          <button
            onClick={() => setShowEncryptionKey(!showEncryptionKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className={`${showEncryptionKey ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
          </button>
        </div>

        <div className="flex items-start gap-2 mt-3 bg-violet-50 rounded-lg p-3">
          <i className="ri-information-line text-violet-600 text-sm mt-0.5"></i>
          <p className="text-xs text-violet-700">
            The encryption key adds an extra layer of security for card payments. Find it alongside your API keys in the{' '}
            <a
              href="https://dashboard.flutterwave.com/settings/apis"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="underline font-medium cursor-pointer"
            >
              Flutterwave Dashboard
            </a>
            .
          </p>
        </div>
      </div>

      {/* Consultation Fee */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <i className="ri-money-dollar-circle-line text-lg text-emerald-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Consultation Fee</h3>
            <p className="text-xs text-gray-500">Set the base price for a consultation session</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Base Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent cursor-pointer bg-white"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.symbol} {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Amount</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">
                {currencyInfo?.symbol}
              </span>
              <input
                type="number"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                min="0"
                step="100"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="50000"
              />
            </div>
          </div>
        </div>

        {fee && Number(fee) > 0 && (
          <div className="mt-4 bg-emerald-50 rounded-xl p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <i className="ri-price-tag-3-line text-sm text-emerald-600"></i>
            </div>
            <div>
              <p className="text-xs text-emerald-700 font-medium">
                Clients will be charged{' '}
                <strong>
                  {currencyInfo?.symbol}
                  {Number(fee).toLocaleString()}
                </strong>{' '}
                {currency} per consultation
              </p>
              <p className="text-[11px] text-emerald-600 mt-0.5">
                Other currencies will be auto-converted at checkout
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        {settings?.updated_at && (
          <p className="text-xs text-gray-400">
            Last updated:{' '}
            {new Date(settings.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        )}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer ml-auto"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line text-base"></i>
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}
